// AssistantTab — chatbot integrado al admin que responde sobre el uso
// del sistema, basado en la KB derivada del manual de usuario.
//
// Motor de búsqueda: matching de tokens con scoring simple (offline).
// Cuando se conecte con RAG, basta con reemplazar la llamada a searchKB
// por un fetch al endpoint del motor RAG; el UI se queda igual.

import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User as UserIcon, Sparkles, HelpCircle, BookOpen } from 'lucide-react';
import { searchKB, topSuggestedTopics } from '../../utils/assistantSearch';
import ManualPanel from './ManualPanel';

const NOT_FOUND_MSG =
  'No tengo información específica sobre eso en mi base de conocimiento actual. ' +
  'Puedes preguntarme sobre roles, cómo crear artículos o ediciones, gestión de ' +
  'usuarios, parámetros editoriales o la diferencia entre edición Nacional y regional. ' +
  'También puedes consultar el Manual de Usuario completo o preguntarle a un Editor.';

export default function AssistantTab() {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    text: 'Hola. Soy el Asistente de Comercio Colaborativo. Puedo ayudarte con dudas sobre el uso del sistema: roles, artículos, ediciones, usuarios, etc. Escribe tu pregunta o toca uno de los temas sugeridos abajo.',
    suggestions: null,
    related: null,
  }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Panel lateral con manual completo. Empieza cerrado.
  const [manualOpen, setManualOpen] = useState(false);
  const listRef = useRef(null);

  // Esc cierra el manual si está abierto (accesibilidad).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setManualOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auto-scroll al final cuando llega un mensaje nuevo
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (text) => {
    const query = (text ?? input).trim();
    if (!query || sending) return;

    setMessages(prev => [...prev, { role: 'user', text: query }]);
    setInput('');
    setSending(true);

    // Pequeña latencia artificial para que se sienta natural
    // (no instantáneo). Aquí es donde en el futuro va la llamada al
    // motor RAG por HTTP.
    await new Promise(r => setTimeout(r, 320));

    const { hit, suggestions } = searchKB(query);

    if (hit) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: hit.answer,
        suggestions: suggestions.map(s => ({ id: s.id, tema: s.tema })),
        related: null,
      }]);
    } else {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: NOT_FOUND_MSG,
        suggestions: null,
        related: null,
      }]);
    }
    setSending(false);
  };

  const askTopic = (topic) => {
    // Cuando el usuario toca un chip de tema sugerido, disparamos
    // exactamente la question de esa entrada (garantiza match perfecto).
    send(topic.question);
  };

  const suggested = topSuggestedTopics(6);

  return (
    <>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 120px)',
      maxWidth: 900,
      margin: '0 auto',
      background: '#fff',
      borderRadius: 8,
      overflow: 'hidden',
      border: '1px solid #e0e0e0',
    }}>
      {/* Cabecera */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e0e0e0',
        background: 'var(--color-primary, #1B4332)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Bot size={22} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Asistente virtual</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Basado en el Manual de Usuario</div>
          </div>
        </div>
        {/* Botón que abre/cierra el panel lateral con el manual. */}
        <button
          onClick={() => setManualOpen(v => !v)}
          title={manualOpen ? 'Ocultar manual' : 'Ver manual completo'}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: manualOpen ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff', padding: '8px 14px', borderRadius: 999,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
          onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
          onMouseOut={e => e.currentTarget.style.background = manualOpen ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)'}
        >
          <BookOpen size={16} />
          {manualOpen ? 'Ocultar manual' : 'Ver manual'}
        </button>
      </div>

      {/* Lista de mensajes */}
      <div ref={listRef} style={{
        flex: 1,
        overflowY: 'auto',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        background: '#f9f9f9',
      }}>
        {messages.map((m, i) => (
          <MessageBubble key={i} message={m} onAskSuggestion={askTopic} />
        ))}
        {sending && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#888', fontSize: 13 }}>
            <Sparkles size={14} /> Buscando…
          </div>
        )}
      </div>

      {/* Chips de temas sugeridos (solo cuando no ha habido conversación real) */}
      {messages.length <= 1 && (
        <div style={{
          padding: '12px 24px',
          background: '#fafafa',
          borderTop: '1px solid #eee',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span style={{ fontSize: 12, color: '#666', width: '100%', marginBottom: 4 }}>
            <HelpCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Temas sugeridos:
          </span>
          {suggested.map(t => (
            <button
              key={t.id}
              onClick={() => askTopic(t)}
              disabled={sending}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderRadius: 999,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 12,
                color: '#333',
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f0f7f4'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              {t.tema}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        style={{
          padding: '12px 16px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 8,
          background: '#fff',
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu pregunta…"
          disabled={sending}
          style={{
            flex: 1,
            padding: '10px 14px',
            border: '1px solid #ccc',
            borderRadius: 999,
            fontSize: 14,
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          style={{
            padding: '10px 18px',
            border: 'none',
            borderRadius: 999,
            background: 'var(--color-primary, #1B4332)',
            color: '#fff',
            cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
            opacity: sending || !input.trim() ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <Send size={16} /> Enviar
        </button>
      </form>

      {/* Disclaimer pequeño */}
      <div style={{
        padding: '6px 24px',
        background: '#fafafa',
        borderTop: '1px solid #f0f0f0',
        fontSize: 11,
        color: '#999',
        textAlign: 'center',
      }}>
        Este asistente responde con base en el Manual de Usuario. Para consultas
        que requieran juicio editorial o soporte técnico, contacta a un Editor.
      </div>
    </div>

    {/* Panel lateral con el manual completo. Se renderiza siempre
        (para respetar transiciones) pero solo pinta cuando manualOpen=true. */}
    <ManualPanel open={manualOpen} onClose={() => setManualOpen(false)} />
    </>
  );
}

function MessageBubble({ message, onAskSuggestion }) {
  const isUser = message.role === 'user';
  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      gap: 10,
    }}>
      {!isUser && (
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--color-primary, #1B4332)',
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Bot size={18} />
        </div>
      )}
      <div style={{ maxWidth: '75%' }}>
        <div style={{
          padding: '10px 14px',
          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          background: isUser ? 'var(--color-primary, #1B4332)' : '#fff',
          color: isUser ? '#fff' : '#222',
          border: isUser ? 'none' : '1px solid #e0e0e0',
          lineHeight: 1.5,
          fontSize: 14,
          whiteSpace: 'pre-wrap',
        }}>
          {message.text}
        </div>
        {message.suggestions && message.suggestions.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#888', width: '100%' }}>
              También te puede interesar:
            </span>
            {message.suggestions.map(s => (
              <button
                key={s.id}
                onClick={() => onAskSuggestion({ question: s.tema })}
                style={{
                  padding: '4px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 999,
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 11,
                  color: '#555',
                }}
                onMouseOver={e => e.currentTarget.style.background = '#f0f7f4'}
                onMouseOut={e => e.currentTarget.style.background = '#fff'}
              >
                {s.tema}
              </button>
            ))}
          </div>
        )}
      </div>
      {isUser && (
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: '#ddd', color: '#555',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <UserIcon size={16} />
        </div>
      )}
    </div>
  );
}
